import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { normalizeAiMessage } from '@/lib/aiMessage';
import type {
  ForecastsIndex,
  RevenueForecast,
  Forecast,
  ReorderSuggestion,
  SlowMover,
  SalesAnomaly,
  AiInsight,
  PeakHour,
  CustomerSegments,
  ShopHealth,
  AiConversation,
  AiMessage,
  ChatQuota,
  AiPeriodComparison,
} from '@/types/ai';

export const CHAT_QUOTA_QUERY_KEY = ['ai', 'chat-quota'] as const;

export function extractChatQuota(
  headers?: Headers | Record<string, string> | null,
): ChatQuota | null {
  if (!headers) return null;

  const read = (name: string): string | null => {
    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
      return headers.get(name);
    }

    const normalizedHeaders = headers as Record<string, string>;
    const lowered = name.toLowerCase();
    const value = normalizedHeaders[lowered] ?? normalizedHeaders[name];
    return typeof value === 'string' && value.length > 0 ? value : null;
  };

  const rawLimit = read('X-Chat-Limit');
  const rawRemaining = read('X-Chat-Remaining');
  if (rawLimit === null && rawRemaining === null) return null;

  const limit = rawLimit !== null ? Number.parseInt(rawLimit, 10) : NaN;
  const remaining = rawRemaining !== null ? Number.parseInt(rawRemaining, 10) : NaN;

  const safeLimit = Number.isNaN(limit) ? null : limit;
  const safeRemaining = Number.isNaN(remaining) ? null : remaining;

  return {
    limit: safeLimit,
    remaining: safeRemaining,
    is_unlimited: safeLimit === 0,
  };
}

export function useAiForecasts(horizon: string = '7d') {
  return useQuery({
    queryKey: ['ai', 'forecasts', horizon],
    queryFn: async () => {
      const response = await api.get<ForecastsIndex>(`/ai/forecasts?horizon=${horizon}`);
      return response.data;
    },
  });
}

export function useAiForecastRevenue(horizon: string = '7d') {
  return useQuery({
    queryKey: ['ai', 'forecast-revenue', horizon],
    queryFn: async () => {
      const response = await api.get<RevenueForecast>(`/ai/forecasts/revenue?horizon=${horizon}`);
      return response.data;
    },
  });
}

export function useAiForecastProduct(productId: string) {
  return useQuery({
    queryKey: ['ai', 'forecast-product', productId],
    queryFn: async () => {
      const response = await api.get<Forecast>(`/ai/forecasts/${productId}`);
      return response.data;
    },
    enabled: !!productId,
  });
}

export function useAiReorderSuggestions() {
  return useQuery({
    queryKey: ['ai', 'reorder-suggestions'],
    queryFn: async () => {
      const response = await api.get<ReorderSuggestion[]>('/ai/reorder-suggestions');
      return response.data;
    },
  });
}

export function useAiSlowMovers(days: number = 30) {
  return useQuery({
    queryKey: ['ai', 'slow-movers', days],
    queryFn: async () => {
      const response = await api.get<SlowMover[]>(`/ai/slow-movers?days=${days}`);
      return response.data;
    },
  });
}

export function useAiAnomalies(days: number = 30) {
  return useQuery({
    queryKey: ['ai', 'anomalies', days],
    queryFn: async () => {
      const response = await api.get<SalesAnomaly[]>(`/ai/anomalies?days=${days}`);
      return response.data;
    },
  });
}

export function useAiInsights(filters?: { type?: string; priority?: string; is_read?: boolean }) {
  const params = new URLSearchParams();
  if (filters?.type) params.set('type', filters.type);
  if (filters?.priority) params.set('priority', filters.priority);
  if (filters?.is_read !== undefined) params.set('is_read', String(filters.is_read ? 1 : 0));
  const qs = params.toString();
  return useQuery({
    queryKey: ['ai', 'insights', filters],
    queryFn: async () => {
      const response = await api.get<AiInsight[]>(`/ai/insights${qs ? `?${qs}` : ''}`);
      return response.data;
    },
  });
}

export function useAiPeakHours(days: number = 30) {
  return useQuery({
    queryKey: ['ai', 'peak-hours', days],
    queryFn: async () => {
      const response = await api.get<PeakHour[]>(`/ai/peak-hours?days=${days}`);
      return response.data;
    },
  });
}

export function useAiCustomerSegments() {
  return useQuery({
    queryKey: ['ai', 'customer-segments'],
    queryFn: async () => {
      const response = await api.get<CustomerSegments>('/ai/customer-segments');
      return response.data;
    },
  });
}

export function useAiShopHealth() {
  return useQuery({
    queryKey: ['ai', 'shop-health'],
    queryFn: async () => {
      const response = await api.get<ShopHealth>('/ai/shop-health');
      return response.data;
    },
  });
}

export function useAiPeriodComparison(period: '7d' | '30d' = '7d') {
  return useQuery({
    queryKey: ['ai', 'period-comparison', period],
    queryFn: async () => {
      const response = await api.get<AiPeriodComparison>(`/ai/period-comparison?period=${period}`);
      return response.data;
    },
  });
}

export function useAiConversations() {
  return useQuery({
    queryKey: ['ai', 'conversations'],
    queryFn: async () => {
      const response = await api.get<AiConversation[]>('/ai/chat');
      return response.data;
    },
  });
}

export function useAiConversation(id: string) {
  return useQuery({
    queryKey: ['ai', 'conversation', id],
    queryFn: async () => {
      const response = await api.get<{ conversation: AiConversation; messages: AiMessage[] }>(
        `/ai/chat/${id}`,
      );
      return {
        conversation: response.data.conversation,
        messages: response.data.messages.map(normalizeAiMessage),
      };
    },
    enabled: !!id,
  });
}

export function useChatQuota() {
  return useQuery({
    queryKey: CHAT_QUOTA_QUERY_KEY,
    queryFn: async () => null as ChatQuota | null,
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: null as ChatQuota | null,
  });
}
